import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { ArrowDownTrayIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useClientsQuery } from '@/features/Clients/Data';
import { useEquipamentosExcluidosQuery, useEquipamentosQuery, useNomesColaboradores } from '../../../../hooks/data';
import {
  aplicarSelecaoAtivos,
  ativoCorrespondeTexto,
  contarAtivos,
  indexarDuplicados,
  pendenciasDoAtivo,
  type ContextoAtivos,
  type SelecaoAtivos,
} from './ativos.utils';
import AtivosResumoPainel from './AtivosResumoPainel';
import AtivoLinha from './AtivoLinha';
import AtivoDrawer from './AtivoDrawer';
import PendenciasFiltroBarra, { atendeFiltroPendenciaAtivo, type FiltroPendenciaAtivo } from './PendenciasFiltroBarra';
import AtivosExcluidosModal from './AtivosExcluidosModal';
import ImportarGlpiModal from './ImportarGlpiModal';
import { CATEGORIAS_ATIVO_ABA, categoriaDoTipo, type CategoriaAtivoAba } from './ativos.definitions';

const SELECAO_VAZIA: SelecaoAtivos = { situacoes: [], tipos: [], propriedades: [], projetos: [] };
const chavePreferencias = (uid: string | undefined) => `si:ativosFiltro:${uid ?? 'anon'}`;
const chaveCategoria = (uid: string | undefined) => `si:ativosCategoria:${uid ?? 'anon'}`;

function lerCategoria(uid: string | undefined): CategoriaAtivoAba {
  try {
    return (localStorage.getItem(chaveCategoria(uid)) as CategoriaAtivoAba | null) ?? 'computadores';
  } catch {
    return 'computadores';
  }
}

function lerPreferencias(uid: string | undefined): Partial<SelecaoAtivos> {
  try {
    const bruto = localStorage.getItem(chavePreferencias(uid));
    return bruto ? JSON.parse(bruto) : {};
  } catch {
    return {};
  }
}

/**
 * Aba "Ativos" — inventário próprio (substituto do GLPI, ver
 * discovery/SI/02-INVENTARIO-PROPRIO-FASE1.md). Mesma coleção que a Custódia já usa
 * (si_equipamentos), agora com a ficha completa. Correção de cadastro antigo não é por
 * script: as pendências aparecem aqui (alerta no topo + ícone na linha) e o SI resolve na
 * ficha. Lista em tempo real — reflete na hora o que as Cloud Functions de sync escreverem.
 */
export default function AtivosSection() {
  const { user } = useAuth();
  const [selecao, setSelecao] = useState<SelecaoAtivos>(() => ({ ...SELECAO_VAZIA, ...lerPreferencias(user?.uid) }));
  const [busca, setBusca] = useState('');
  const [filtroPendencia, setFiltroPendencia] = useState<FiltroPendenciaAtivo>('nenhum');
  const [aberto, setAberto] = useState<string | null>(null);
  const [importarAberto, setImportarAberto] = useState(false);
  const [excluidosAberto, setExcluidosAberto] = useState(false);
  const { data: excluidos } = useEquipamentosExcluidosQuery();
  // Sub-aba por categoria (igual às do Inventário/GLPI) — lembrada por pessoa.
  const [categoria, setCategoria] = useState<CategoriaAtivoAba>(() => lerCategoria(user?.uid));
  const trocarCategoria = (nova: CategoriaAtivoAba) => {
    setCategoria(nova);
    // Tipos marcados são de uma categoria só (ex.: Macbook) — não fazem sentido na próxima.
    setSelecao((atual) => ({ ...atual, tipos: [] }));
    try {
      localStorage.setItem(chaveCategoria(user?.uid), nova);
    } catch {
      // não é crítico — só não persiste.
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem(chavePreferencias(user?.uid), JSON.stringify(selecao));
    } catch {
      // não é crítico — só não persiste.
    }
  }, [user?.uid, selecao]);

  const { data: ativos, isLoading } = useEquipamentosQuery();
  const { data: clientes = [] } = useClientsQuery();
  // Inclui quem não está mais ativo ("Fulano (inativo)") — ativo de desligado não fica sem nome.
  const nomeColaboradorPorId = useNomesColaboradores();

  const contexto = useMemo<ContextoAtivos>(
    () => ({
      nomeClientePorId: new Map(clientes.map((c) => [c.id, c.name])),
      nomeColaboradorPorId,
    }),
    [clientes, nomeColaboradorPorId]
  );

  const pendenciasPorId = useMemo(() => {
    const indice = indexarDuplicados(ativos);
    return new Map(ativos.map((a) => [a.id, pendenciasDoAtivo(a, indice)]));
  }, [ativos]);

  // Quantidade e pendência por categoria — pra mostrar na sub-aba sem precisar abrir.
  const resumoPorCategoria = useMemo(() => {
    const resumo = new Map<CategoriaAtivoAba, { total: number; comPendencia: number }>();
    for (const a of ativos) {
      const c = categoriaDoTipo(a.tipo);
      const atual = resumo.get(c) ?? { total: 0, comPendencia: 0 };
      atual.total += 1;
      if ((pendenciasPorId.get(a.id)?.length ?? 0) > 0) atual.comPendencia += 1;
      resumo.set(c, atual);
    }
    return resumo;
  }, [ativos, pendenciasPorId]);
  const abasCategoria = [
    ...CATEGORIAS_ATIVO_ABA,
    ...((resumoPorCategoria.get('outros')?.total ?? 0) > 0 ? [{ id: 'outros' as const, label: 'Outros', tipos: [] }] : []),
  ];
  const categoriaAtual = CATEGORIAS_ATIVO_ABA.find((c) => c.id === categoria);

  // Com texto na busca, procura em todos os tipos e Situações (aba e painel ficam de fora) —
  // senão um ativo de outra aba, ou escondido por um filtro do painel guardado da última
  // visita, simplesmente "não existia".
  const buscando = busca.trim().length > 0;
  const base = useMemo(
    () =>
      ativos.filter(
        (a) =>
          (buscando || categoriaDoTipo(a.tipo) === categoria) &&
          ativoCorrespondeTexto(a, busca, contexto) &&
          atendeFiltroPendenciaAtivo(pendenciasPorId.get(a.id), filtroPendencia)
      ),
    [ativos, categoria, busca, buscando, contexto, filtroPendencia, pendenciasPorId]
  );
  const exibidos = useMemo(
    () =>
      (buscando ? [...base] : aplicarSelecaoAtivos(base, selecao)).sort((a, b) =>
        (a.patrimonio ?? '').localeCompare(b.patrimonio ?? '', 'pt-BR', { numeric: true })
      ),
    [base, selecao, buscando]
  );
  const contagens = useMemo(() => contarAtivos(base, selecao), [base, selecao]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          className="input-primary w-full max-w-sm"
          placeholder="Pesquise aqui... (patrimônio, série, modelo, colaborador)"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        {excluidos.length > 0 && (
          <button type="button" className="ml-auto text-sm text-gray-500 hover:underline" onClick={() => setExcluidosAberto(true)}>
            Excluídos ({excluidos.length})
          </button>
        )}
        <button
          type="button"
          className={clsx('btn-secondary flex items-center gap-2', excluidos.length === 0 && 'ml-auto')}
          onClick={() => setImportarAberto(true)}
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
          Importar do GLPI
        </button>
        <button type="button" className="btn-primary flex items-center gap-2" onClick={() => setAberto('novo')}>
          <PlusIcon className="h-5 w-5" />
          Novo ativo
        </button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {abasCategoria.map((aba) => {
          const resumo = resumoPorCategoria.get(aba.id);
          return (
            <button
              key={aba.id}
              type="button"
              onClick={() => trocarCategoria(aba.id)}
              className={clsx(
                'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium',
                categoria === aba.id ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              {aba.label}
              <span className="rounded-full bg-gray-100 px-1.5 text-xs text-gray-600">{resumo?.total ?? 0}</span>
              {(resumo?.comPendencia ?? 0) > 0 && (
                <span className="h-2 w-2 rounded-full bg-red-500" title={`${resumo?.comPendencia} com pendência`} />
              )}
            </button>
          );
        })}
      </div>

      {buscando && (
        <p className="text-sm text-gray-600">
          Buscando em <strong>todos os tipos e situações</strong> (a aba e os filtros do painel não se aplicam):{' '}
          {exibidos.length} {exibidos.length === 1 ? 'resultado' : 'resultados'}.
        </p>
      )}
      <PendenciasFiltroBarra pendenciasPorId={pendenciasPorId} filtro={filtroPendencia} onChange={setFiltroPendencia} />

      {!isLoading && (
        <AtivosResumoPainel
          contagens={contagens}
          selecao={selecao}
          onChange={(parcial) => setSelecao((atual) => ({ ...atual, ...parcial }))}
          clientes={clientes}
          // Linha de Tipo só onde a categoria tem mais de um tipo (Computadores: notebook, Macbook...).
          mostrarTipo={!categoriaAtual || categoriaAtual.tipos.length > 1}
        />
      )}

      {isLoading && <p className="text-sm text-gray-500">Carregando...</p>}
      {!isLoading && exibidos.length === 0 && (
        <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          Nenhum ativo encontrado.
        </p>
      )}
      <div className="space-y-2">
        {exibidos.map((ativo) => (
          <AtivoLinha
            key={ativo.id}
            ativo={ativo}
            pendencias={pendenciasPorId.get(ativo.id) ?? []}
            contexto={contexto}
            onSelect={setAberto}
          />
        ))}
      </div>

      <ImportarGlpiModal isOpen={importarAberto} onClose={() => setImportarAberto(false)} contexto={contexto} />
      <AtivosExcluidosModal
        isOpen={excluidosAberto}
        onClose={() => setExcluidosAberto(false)}
        excluidos={excluidos}
        onAbrir={(id) => {
          setExcluidosAberto(false);
          setAberto(id);
        }}
      />

      <AtivoDrawer
        aberto={aberto}
        ativos={ativos}
        excluidos={excluidos}
        pendenciasPorId={pendenciasPorId}
        contexto={contexto}
        onAbrir={setAberto}
        onClose={() => setAberto(null)}
      />
    </div>
  );
}
